import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { ArrowLeft, KeyRound, Mail, Save, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { portalQueryKeys, useIdentityQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { profileInitials, validateDisplayName } from "@/lib/profile";

export const Route = createFileRoute("/profile")({ component: ProfilePage });

function ProfilePage() {
  const router = useRouter();
  const identity = useIdentityQuery();
  const { auth, client, tenantContext } = usePortalRuntime();
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (identity.data?.displayName) setDisplayName(identity.data.displayName);
    if (!auth) return () => undefined;
    void auth
      .getProfile()
      .then((profile) => {
        if (!active || !profile.displayName) return;
        setDisplayName(profile.displayName);
        router.options.context.queryClient.setQueryData(portalQueryKeys.identity, (current) =>
          current && typeof current === "object"
            ? { ...current, displayName: profile.displayName }
            : current,
        );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [auth, identity.data?.displayName, router.options.context.queryClient]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    const result = validateDisplayName(displayName);
    if ("error" in result && result.error) {
      setFieldError(result.error);
      return;
    }
    if (!auth) {
      toast.info("Profile editing is available after signing into the live subscriber portal.");
      return;
    }
    setSaving(true);
    setFieldError(null);
    try {
      await auth.updateProfile({ displayName: result.displayName });
      setDisplayName(result.displayName);
      router.options.context.queryClient.setQueryData(portalQueryKeys.identity, (current) =>
        current && typeof current === "object"
          ? { ...current, displayName: result.displayName }
          : current,
      );
      toast.success("Profile updated");
    } catch {
      toast.error("Your profile could not be updated. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function updatePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    if (password.length < 12 || password.length > 128) {
      setPasswordError("Use a password of 12–128 characters.");
      return;
    }
    if (password !== passwordConfirmation) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (!auth || client.mode !== "live") {
      setPassword("");
      setPasswordConfirmation("");
      toast.info("Password changes are available after signing into the live subscriber portal.");
      return;
    }
    setUpdatingPassword(true);
    try {
      await auth.updatePassword(password);
      setPassword("");
      setPasswordConfirmation("");
      toast.success("Password updated");
    } catch {
      setPassword("");
      setPasswordConfirmation("");
      toast.error("Your password could not be updated. Sign in again or use password recovery.");
    } finally {
      setUpdatingPassword(false);
    }
  }

  async function sendPasswordReset() {
    if (!auth || !identity.data?.email) return;
    setSendingReset(true);
    try {
      await auth.requestPasswordRecovery(identity.data.email, window.location.origin);
      toast.success("Password reset email sent");
    } catch {
      toast.error("A password reset email could not be sent. Try again later.");
    } finally {
      setSendingReset(false);
    }
  }

  const email = identity.data?.email ?? "";
  const canEdit = client.mode === "live" && Boolean(auth);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <div>
            <div className="text-sm font-semibold">Subscriber profile</div>
            <div className="text-xs text-muted-foreground">Identity and account security</div>
          </div>
          <div className="ml-auto">
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-6 px-6 py-8 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle>Profile details</CardTitle>
            <CardDescription>
              Manage the name shown in Broadview. Email and access assignments are
              administrator-managed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={saveProfile}>
              <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-lg font-semibold text-primary">
                  {profileInitials(displayName || identity.data?.displayName, email)}
                </div>
                <div>
                  <p className="font-medium">
                    {displayName || identity.data?.displayName || "Subscriber"}
                  </p>
                  <p className="text-sm text-muted-foreground">{email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display-name">Full name</Label>
                <Input
                  id="display-name"
                  autoComplete="name"
                  maxLength={200}
                  value={displayName}
                  disabled={!canEdit || saving}
                  onChange={(event) => setDisplayName(event.target.value)}
                  aria-invalid={Boolean(fieldError)}
                  aria-describedby={fieldError ? "display-name-error" : undefined}
                />
                {fieldError && (
                  <p id="display-name-error" className="text-sm text-destructive">
                    {fieldError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input id="profile-email" className="pl-9" value={email} readOnly />
                </div>
                <p className="text-xs text-muted-foreground">
                  Contact a Broadview administrator to change your sign-in email.
                </p>
              </div>

              <Button type="submit" disabled={!canEdit || saving}>
                <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" /> Broadview access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <ProfileFact label="Workspace" value="Subscriber portal" />
              <ProfileFact label="Division" value={tenantContext.tenantName} />
              <ProfileFact label="Status" value="Active" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <KeyRound className="h-4 w-4 text-primary" /> Account security
              </CardTitle>
              <CardDescription>
                Change your password directly while signed in. Email recovery remains available if
                you cannot use the current session.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <form className="space-y-3" onSubmit={updatePassword}>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    value={password}
                    disabled={!canEdit || updatingPassword}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    value={passwordConfirmation}
                    disabled={!canEdit || updatingPassword}
                    onChange={(event) => setPasswordConfirmation(event.target.value)}
                  />
                </div>
                {passwordError && (
                  <p className="text-sm text-destructive" role="alert">
                    {passwordError}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={!canEdit || updatingPassword}>
                  {updatingPassword ? "Updating…" : "Update password"}
                </Button>
              </form>
              <div className="border-t border-border pt-4">
                <p className="mb-3 text-xs text-muted-foreground">
                  Cannot change it from this session? Send a secure recovery link to your sign-in
                  email.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={!canEdit || sendingReset}
                  onClick={() => void sendPasswordReset()}
                >
                  {sendingReset ? "Sending…" : "Send password reset email"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function ProfileFact({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 flex items-center gap-2 font-medium">
        <UserRound className="h-3.5 w-3.5 text-muted-foreground" /> {value}
      </p>
    </div>
  );
}
